import React from "react";
import { Badge, Bleed, BlockStack, Card, DataTable, Divider, InlineStack, Layout, Page, SkeletonBodyText, SkeletonDisplayText, SkeletonTabs, Text } from "@shopify/polaris";
import { DI, DIProps } from "../../../../../Core";
import { ShopifySquareIcon } from "../../../../../Asests/svgIcon/icon";
import { formatDateForOrderCreation } from "../Helper";
import { Table } from "antd";
import { useTranslation } from "react-i18next";

interface props extends DIProps {
    row: any
    months: string[]
    country: string
}

const OrderViewSkeleton = (props: props) => {
    const { t } = useTranslation();
    const { row, months, country } = props;
    const columns = [
        {
            title: <SkeletonBodyText lines={1} />,
            dataIndex: 'title',
            key: 'title',
            width: '200px',
        },
        {
            title: <SkeletonBodyText lines={1} />,
            dataIndex: 'product',
            key: 'product',
            width: '150px',
        },
        {
            title: <SkeletonBodyText lines={1} />,
            key: 'quantity',
            dataIndex: 'quantity',
            width: '100px',
        },
        {
            title: <BlockStack gap="0">
                <SkeletonBodyText lines={2} />
            </BlockStack>,
            key: 'price',
            dataIndex: 'price',
            width: '100px',
        },
        {
            title: <BlockStack gap="0">
                <SkeletonBodyText lines={2} />
            </BlockStack>,
            key: 'vat',
            dataIndex: 'vat',
            width: '100px',
        },
    ];

    const dataSource = [
        {
            key: 1,
            title: <SkeletonBodyText lines={3} />,
            product: <BlockStack gap="0">
                <SkeletonBodyText lines={1} />
            </BlockStack>,
            quantity: <SkeletonBodyText lines={1} />,
            price: <SkeletonBodyText lines={1} />,
            vat: <SkeletonBodyText lines={1} />,
        },
    ];
    const rows = [
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
        [<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />],
    ];

    const amazonDataTable = <Card padding='0' roundedAbove='sm'>
        <div className='Custom__Data--Table'>
            <DataTable
                showTotalsInFooter
                columnContentTypes={['text', 'numeric', 'numeric',]}
                headings={[<SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />, <SkeletonBodyText lines={1} />,]}
                rows={rows}
                totals={['', '', <SkeletonBodyText lines={1} />]}
                totalsName={{ singular: <SkeletonBodyText lines={1} />, plural: <SkeletonBodyText lines={1} />, }}
            />
        </div>
    </Card>

    return (
        <Page
            fullWidth
            backAction={{ content: 'orderGrid', onAction: () => props.history('../allSales') }}
            title={props.match?.['o_id'] || "N/A"}
            titleMetadata={
                <InlineStack gap="600" align='center'>
                    <InlineStack gap="200">
                        <Badge tone='new'> - </Badge>
                        <Badge tone='new'> - </Badge>
                    </InlineStack>
                    <div className='Custom__Divider--Section'>
                        <InlineStack gap="200" blockAlign='center'>
                            <ShopifySquareIcon />
                            <Text as={'span'}>-</Text>
                        </InlineStack>
                    </div>
                </InlineStack>
            }
            subtitle={row?.source_created_at ? `${formatDateForOrderCreation(months, row?.source_created_at, t)} from Amazon (via import)` : "N/A"}
            compactTitle
            secondaryActions={['FR', 'IN', 'IT', 'ES', 'GB', 'US'].includes(country) ? [{ content: 'Purchase Buy shipping', disabled: true }] : []}
            pagination={{ hasPrevious: false, onPrevious: () => { }, onNext: () => { }, hasNext: false, }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="500">
                            <SkeletonDisplayText size="small" />
                            <BlockStack gap="400">
                                <SkeletonTabs />
                                <BlockStack gap="500">
                                    <Card padding="0" roundedAbove='sm'>
                                        <Table columns={columns} dataSource={dataSource} pagination={false} size='small' scroll={{ x: 700 }} />
                                    </Card>
                                    <Card padding="0" roundedAbove='sm'>{amazonDataTable}</Card>
                                </BlockStack>
                            </BlockStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>
                <Layout.Section variant="oneThird">
                    <BlockStack gap="400">
                        <Card roundedAbove='sm'>
                            <BlockStack gap="500">
                                <SkeletonDisplayText size="small" />
                                <Bleed marginInline="500"><Divider /></Bleed>
                                <React.Fragment>
                                    <BlockStack gap="0"><SkeletonBodyText /></BlockStack>
                                    <BlockStack gap="0"><SkeletonBodyText /></BlockStack>
                                </React.Fragment>
                                <BlockStack gap="0"><SkeletonBodyText /></BlockStack>
                            </BlockStack>
                        </Card>
                        <Card roundedAbove='sm'>
                            <BlockStack gap="500">
                                <SkeletonDisplayText size="small" />
                                <React.Fragment >
                                    <Bleed marginInline="500"><Divider /></Bleed>
                                    <BlockStack gap="0"><SkeletonBodyText lines={4} /></BlockStack>
                                    <BlockStack gap="0"><SkeletonBodyText lines={2} /></BlockStack>
                                </React.Fragment>
                            </BlockStack>
                        </Card>
                        <Card roundedAbove='sm'>
                            <BlockStack gap="500">
                                <SkeletonDisplayText size="small" />
                                <Bleed marginInline="500"><Divider /></Bleed>
                                <div className='Custom__TagsOrderView--Skeleton'><SkeletonBodyText lines={1} /></div>
                            </BlockStack>
                        </Card>
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
}

export default DI(OrderViewSkeleton);